import { useState, useMemo, useCallback } from "react";
import { type UserStory } from "@/lib/projectStore";
import { format, addDays, addWeeks, addMonths, differenceInDays, parseISO, isAfter, isBefore, startOfDay, startOfMonth, startOfWeek, getQuarter, getYear, eachMonthOfInterval } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CalendarIcon, AlertTriangle, ArrowRight, Link2, Zap, Clock, Flag, Plus, X, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

/* ─── Milestone hints ─── */
export interface Milestone {
  id: string;
  name: string;
  date: string;
}

const MILESTONE_HINTS: { category: string; names: string[] }[] = [
  {
    category: "Regulatory",
    names: ["FDA 510(k) Submission", "FDA PMA Submission", "CE Mark Submission", "MDR Submission", "TGA Submission", "ISO 13485 Audit"],
  },
  {
    category: "Development",
    names: ["Design Freeze", "Code Freeze", "Architecture Review", "Security Audit", "Design Transfer"],
  },
  {
    category: "V&V",
    names: ["V&V Testing Start", "V&V Testing Complete", "Clinical Trial Start", "Clinical Trial End", "UAT Complete"],
  },
  {
    category: "Release",
    names: ["Go-Live", "Commercial Release", "Beta Launch", "Post-Market Surveillance Start"],
  },
];

/* ─── Constants ─── */
const POINTS_TO_DAYS: Record<number, number> = {
  1: 1, 2: 1.5, 3: 2, 5: 4, 8: 6, 13: 10, 21: 16,
};

const SHIFT_DELTAS = [-14, -7, 7, 14, 21];
const SHIFT_REASONS = [
  "Scope grew",
  "Dependency delayed",
  "Stakeholder feedback",
  "Capacity issue",
  "Technical complexity",
  "External blocker",
  "Other",
];

interface SprintShiftEntry {
  delta: number;
  reason: string;
  appliedAt: string;
}

function estimateDays(storyPoints?: number): number {
  if (!storyPoints) return 3;
  return POINTS_TO_DAYS[storyPoints] || Math.ceil(storyPoints * 0.8);
}

const priorityOrder: Record<string, number> = { must: 0, should: 1, could: 2, wont: 3 };

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
  projectStartDate?: string;
  sprintLengthWeeks?: number;
  onSprintLengthChange?: (weeks: number) => void;
  milestones?: Milestone[];
  onMilestonesChange?: (milestones: Milestone[]) => void;
  baProjectId?: string | null;
}

export default function TimelinePanel({ stories, onChange, projectStartDate, sprintLengthWeeks = 2, onSprintLengthChange, milestones = [], onMilestonesChange, baProjectId }: Props) {
  const { token } = useAuth();
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);
  const [linkingFrom, setLinkingFrom] = useState<string | null>(null);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [viewScale, setViewScale] = useState<"days" | "weeks" | "months" | "quarters">("days");
  const [newMilestoneName, setNewMilestoneName] = useState("");
  const [newMilestoneDate, setNewMilestoneDate] = useState<Date | undefined>();
  const [showHintCategory, setShowHintCategory] = useState<string | null>(null);
  const [selectedSprintNum, setSelectedSprintNum] = useState<number | null>(null);
  const [shiftDelta, setShiftDelta] = useState(7);
  const [shiftReason, setShiftReason] = useState("");
  const [shiftNote, setShiftNote] = useState("");
  const [sprintShiftLog, setSprintShiftLog] = useState<Map<number, SprintShiftEntry[]>>(new Map());

  const today = startOfDay(new Date());
  const baseDate = projectStartDate ? parseISO(projectStartDate) : today;

  /* ─── Auto-suggest dates for stories without them ─── */
  const autoSchedule = useCallback(() => {
    const sorted = [...stories].sort((a, b) => {
      const pa = priorityOrder[a.priority] ?? 2;
      const pb = priorityOrder[b.priority] ?? 2;
      return pa - pb;
    });

    let cursor = baseDate;
    const updated = sorted.map((story) => {
      const days = story.estimatedDays || estimateDays(story.storyPoints);
      const start = story.startDate ? parseISO(story.startDate) : cursor;
      const due = addDays(start, days);
      cursor = due;
      return {
        ...story,
        startDate: story.startDate || start.toISOString(),
        dueDate: story.dueDate || due.toISOString(),
        estimatedDays: days,
        originalDueDate: story.originalDueDate || due.toISOString(),
      };
    });
    onChange(updated);
  }, [stories, onChange, baseDate]);

  /* ─── Cascade: when a story's due date shifts, push dependents ─── */
  const cascadeDates = useCallback(
    (changedId: string, newDueDate: Date, currentStories: UserStory[]): UserStory[] => {
      let result = [...currentStories];

      // Find all stories that depend on the changed one
      const dependents = result.filter(
        (s) => s.dependencies?.includes(changedId)
      );

      for (const dep of dependents) {
        const depStart = dep.startDate ? parseISO(dep.startDate) : today;
        // If the new due date of parent is after dependent's start, push it
        if (isAfter(newDueDate, depStart)) {
          const newStart = addDays(newDueDate, 1);
          const days = dep.estimatedDays || estimateDays(dep.storyPoints);
          const newDue = addDays(newStart, days);
          const idx = result.findIndex((s) => s.id === dep.id);
          if (idx !== -1) {
            result[idx] = {
              ...result[idx],
              startDate: newStart.toISOString(),
              dueDate: newDue.toISOString(),
            };
            // Recursively cascade
            result = cascadeDates(dep.id, newDue, result);
          }
        }
      }
      return result;
    },
    [today]
  );

  const handleDateChange = (storyId: string, field: "startDate" | "dueDate", date: Date) => {
    let updated = stories.map((s) => {
      if (s.id !== storyId) return s;
      const newStory = { ...s, [field]: date.toISOString() };
      // If start date changed, auto-adjust due date
      if (field === "startDate") {
        const days = s.estimatedDays || estimateDays(s.storyPoints);
        newStory.dueDate = addDays(date, days).toISOString();
      }
      // If due date changed, recalc estimated days
      if (field === "dueDate" && s.startDate) {
        newStory.estimatedDays = differenceInDays(date, parseISO(s.startDate));
      }
      return newStory;
    });

    // Cascade to dependents
    if (field === "dueDate") {
      updated = cascadeDates(storyId, date, updated);
    } else {
      const story = updated.find((s) => s.id === storyId);
      if (story?.dueDate) {
        updated = cascadeDates(storyId, parseISO(story.dueDate), updated);
      }
    }

    onChange(updated);
  };

  const handleAddDependency = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    onChange(
      stories.map((s) => {
        if (s.id !== toId) return s;
        const deps = s.dependencies || [];
        if (deps.includes(fromId)) return s;
        return { ...s, dependencies: [...deps, fromId] };
      })
    );
    setLinkingFrom(null);
  };

  const removeDependency = (storyId: string, depId: string) => {
    onChange(
      stories.map((s) => {
        if (s.id !== storyId) return s;
        return { ...s, dependencies: (s.dependencies || []).filter((d) => d !== depId) };
      })
    );
  };

  /* ─── Sprint shift ─── */
  const applySprintShift = useCallback(async () => {
    if (selectedSprintNum === null || !shiftReason) return;
    const deltaMs = shiftDelta * 24 * 60 * 60 * 1000;

    // Determine which stories start on or after the selected sprint start day
    const sprintDaysLocal = sprintLengthWeeks * 7;
    const shiftFromDay = (selectedSprintNum - 1) * sprintDaysLocal;
    const shiftFromDate = addDays(baseDate, shiftFromDay);

    const updated = stories.map((story) => {
      if (!story.startDate) return story;
      if (isBefore(parseISO(story.startDate), shiftFromDate)) return story;
      return {
        ...story,
        startDate: new Date(new Date(story.startDate).getTime() + deltaMs).toISOString(),
        dueDate: story.dueDate ? new Date(new Date(story.dueDate).getTime() + deltaMs).toISOString() : story.dueDate,
        originalDueDate: story.originalDueDate ? new Date(new Date(story.originalDueDate).getTime() + deltaMs).toISOString() : story.originalDueDate,
      };
    });
    onChange(updated);

    const entry: SprintShiftEntry = {
      delta: shiftDelta,
      reason: shiftReason + (shiftNote ? `: ${shiftNote}` : ""),
      appliedAt: new Date().toISOString(),
    };
    setSprintShiftLog((prev) => {
      const next = new Map(prev);
      next.set(selectedSprintNum, [...(next.get(selectedSprintNum) || []), entry]);
      return next;
    });

    if (baProjectId && token) {
      const sign = shiftDelta >= 0 ? "+" : "";
      const label = Math.abs(shiftDelta) % 7 === 0 ? `${sign}${shiftDelta / 7}w` : `${sign}${shiftDelta}d`;
      await axios.post(
        `${BASE}/api/tools/decisions`,
        {
          project_id: baProjectId,
          title: `Sprint ${selectedSprintNum} shifted ${label}`,
          decision_text: shiftReason,
          rationale: shiftNote || undefined,
          status: "made",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => {});
    }

    setSelectedSprintNum(null);
    setShiftReason("");
    setShiftNote("");
  }, [selectedSprintNum, shiftDelta, shiftReason, shiftNote, sprintLengthWeeks, baseDate, stories, onChange, baProjectId, token]);

  /* ─── Timeline rendering ─── */
  const scheduledStories = stories.filter((s) => s.startDate && s.dueDate);
  const unscheduledStories = stories.filter((s) => !s.startDate || !s.dueDate);

  const allDates = scheduledStories.flatMap((s) => [parseISO(s.startDate!), parseISO(s.dueDate!)]);
  const timelineStart = allDates.length > 0
    ? startOfDay(allDates.reduce((a, b) => (isBefore(a, b) ? a : b)))
    : today;
  const timelineEnd = allDates.length > 0
    ? startOfDay(addDays(allDates.reduce((a, b) => (isAfter(a, b) ? a : b)), 1))
    : addDays(today, 30);
  const totalDays = Math.max(differenceInDays(timelineEnd, timelineStart), 7);

  const storyById = new Map(stories.map((s) => [s.id, s]));

  /* ─── Sprint boundaries ─── */
  const sprintDays = sprintLengthWeeks * 7;
  const sprintColors = [
    "bg-accent/[0.06]",
    "bg-primary/[0.04]",
    "bg-accent/[0.06]",
    "bg-primary/[0.04]",
  ];
  const sprintBorderColors = [
    "text-accent",
    "text-primary",
    "text-accent",
    "text-primary",
  ];
  const sprints: { number: number; startPct: number; widthPct: number; label: string; bgClass: string; borderClass: string }[] = [];
  if (sprintDays > 0) {
    let sprintStart = 0;
    let sprintNum = 1;
    while (sprintStart < totalDays) {
      const sprintEnd = Math.min(sprintStart + sprintDays, totalDays);
      const colorIdx = (sprintNum - 1) % sprintColors.length;
      sprints.push({
        number: sprintNum,
        startPct: (sprintStart / totalDays) * 100,
        widthPct: ((sprintEnd - sprintStart) / totalDays) * 100,
        label: `S${sprintNum}`,
        bgClass: sprintColors[colorIdx],
        borderClass: sprintBorderColors[colorIdx],
      });
      sprintStart = sprintEnd;
      sprintNum++;
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
            Timeline
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {scheduledStories.length} scheduled · {unscheduledStories.length} unscheduled ·{" "}
            {stories.reduce((a, s) => a + (s.storyPoints || 0), 0)} total pts
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {linkingFrom && (
            <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/30 gap-1">
              <Link2 className="h-3 w-3" /> Click a story to link dependency
              <button onClick={() => setLinkingFrom(null)} className="ml-1 hover:text-destructive">✕</button>
            </Badge>
          )}
          {/* View scale */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">View</span>
            <div className="flex items-center border border-border rounded-md overflow-hidden">
              {(["days", "weeks", "months", "quarters"] as const).map((scale) => (
                <button
                  key={scale}
                  onClick={() => setViewScale(scale)}
                  className={cn(
                    "px-2.5 py-1 text-[10px] font-mono transition-colors",
                    viewScale === scale
                      ? "bg-accent text-accent-foreground font-bold"
                      : "text-muted-foreground hover:bg-secondary"
                  )}
                >
                  {scale === "days" ? "Day" : scale === "weeks" ? "Wk" : scale === "months" ? "Mo" : "Qtr"}
                </button>
              ))}
            </div>
          </div>
          {/* Sprint length — only useful in day/week view */}
          {(viewScale === "days" || viewScale === "weeks") && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Sprint</span>
              <div className="flex items-center border border-border rounded-md overflow-hidden">
                {[1, 2, 3, 4].map((w) => (
                  <button
                    key={w}
                    onClick={() => onSprintLengthChange?.(w)}
                    className={cn(
                      "px-2.5 py-1 text-[10px] font-mono transition-colors",
                      sprintLengthWeeks === w
                        ? "bg-accent text-accent-foreground font-bold"
                        : "text-muted-foreground hover:bg-secondary"
                    )}
                  >
                    {w}w
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={autoSchedule} className="gap-1.5 text-xs">
                  <Zap className="h-3.5 w-3.5" /> Auto-Schedule
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                Assigns dates by priority order using story points (1pt≈1d, 3pt≈2d, 5pt≈4d, 8pt≈6d, 13pt≈10d). Adjust any date and dependent items cascade automatically.
              </TooltipContent>
            </Tooltip>
            <Button variant="outline" size="sm" onClick={() => setShowMilestoneForm(v => !v)} className="gap-1.5 text-xs">
              <Flag className="h-3.5 w-3.5" /> Milestones
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <button className="text-muted-foreground hover:text-foreground transition-colors">
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs text-xs">
                Use <strong>Auto-Schedule</strong> to assign dates, then click any story bar to edit dates or add dependencies. Use the link icon to chain stories — date changes cascade automatically.
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Milestone manager */}
      {showMilestoneForm && (
        <div className="border border-border rounded-md p-3 bg-card space-y-3">
          <p className="text-xs font-semibold font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Flag className="h-3.5 w-3.5" style={{ color: "#314E6E" }} /> Milestones
          </p>

          {/* Existing milestones */}
          {milestones.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {milestones.map(m => (
                <div key={m.id} className="flex items-center gap-1 border rounded px-2 py-0.5 text-[11px] font-mono" style={{ borderColor: "#D9D4CC", backgroundColor: "#F7F4EE", color: "#314E6E" }}>
                  <span style={{ color: "#314E6E" }}>◆</span>
                  <span className="font-semibold">{m.name}</span>
                  <span style={{ color: "#9B978F" }}>· {format(parseISO(m.date), "MMM d, yyyy")}</span>
                  <button onClick={() => onMilestonesChange?.(milestones.filter(x => x.id !== m.id))} className="ml-1 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add form */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Milestone name…"
                value={newMilestoneName}
                onChange={e => setNewMilestoneName(e.target.value)}
                className="h-8 text-xs flex-1"
              />
              <DatePickerField label="" value={newMilestoneDate} onChange={setNewMilestoneDate} />
              <Button
                size="sm"
                className="h-8 text-xs gap-1"
                disabled={!newMilestoneName.trim() || !newMilestoneDate}
                onClick={() => {
                  if (!newMilestoneName.trim() || !newMilestoneDate) return;
                  onMilestonesChange?.([...milestones, { id: crypto.randomUUID(), name: newMilestoneName.trim(), date: newMilestoneDate.toISOString() }]);
                  setNewMilestoneName("");
                  setNewMilestoneDate(undefined);
                }}
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </Button>
            </div>

            {/* Hint categories */}
            <div className="space-y-1.5">
              <p className="text-[10px] text-muted-foreground font-mono uppercase">Common milestones — click to use:</p>
              {MILESTONE_HINTS.map(cat => (
                <div key={cat.category}>
                  <button
                    className="text-[10px] font-semibold text-muted-foreground hover:text-foreground uppercase tracking-wider mb-1 flex items-center gap-1"
                    onClick={() => setShowHintCategory(showHintCategory === cat.category ? null : cat.category)}
                  >
                    {showHintCategory === cat.category ? "▾" : "▸"} {cat.category}
                  </button>
                  {showHintCategory === cat.category && (
                    <div className="flex flex-wrap gap-1 pl-3">
                      {cat.names.map(n => (
                        <button
                          key={n}
                          onClick={() => setNewMilestoneName(n)}
                          className={cn(
                            "text-[10px] px-2 py-0.5 rounded border transition-colors",
                            newMilestoneName === n
                              ? "font-semibold"
                              : "border-border text-muted-foreground hover:text-foreground"
                          )}
                          style={newMilestoneName === n ? { backgroundColor: "#EEF2F8", borderColor: "#314E6E", color: "#314E6E" } : {}}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Sprint shift panel */}
      {selectedSprintNum !== null && (
        <div className="border border-border rounded-lg p-4 bg-card space-y-3 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Shift Sprint {selectedSprintNum} — and all that follow</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Stories from this sprint onward will cascade automatically.</p>
            </div>
            <button onClick={() => { setSelectedSprintNum(null); setShiftReason(""); setShiftNote(""); }}
              className="text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Delta selector */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider w-12">By</span>
            <div className="flex gap-1.5">
              {SHIFT_DELTAS.map((d) => (
                <button
                  key={d}
                  onClick={() => setShiftDelta(d)}
                  className={cn(
                    "px-3 py-1.5 rounded-md text-[11px] font-mono border transition-colors",
                    shiftDelta === d
                      ? "bg-accent text-accent-foreground border-accent font-bold"
                      : "border-border text-muted-foreground hover:border-accent/50"
                  )}
                >
                  {d > 0 ? "+" : ""}{Math.abs(d) % 7 === 0 ? `${d / 7}w` : `${d}d`}
                </button>
              ))}
            </div>
          </div>

          {/* Reason picker */}
          <div>
            <span className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider block mb-2">Why did this shift?</span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {SHIFT_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setShiftReason(r)}
                  className={cn(
                    "text-left px-3 py-2 rounded-md text-[12px] border transition-colors",
                    shiftReason === r
                      ? "bg-accent/10 border-accent text-accent font-medium"
                      : "border-border text-muted-foreground hover:border-accent/40 hover:text-foreground"
                  )}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Optional note */}
          <Input
            placeholder="Add context — what specifically caused this? (optional)"
            value={shiftNote}
            onChange={(e) => setShiftNote(e.target.value)}
            className="h-8 text-xs"
          />

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={!shiftReason}
              onClick={applySprintShift}
              className="gap-1.5 text-xs"
            >
              <ArrowRight className="h-3.5 w-3.5" />
              Apply — Sprint {selectedSprintNum}+ cascades
            </Button>
            <Button variant="ghost" size="sm" className="text-xs"
              onClick={() => { setSelectedSprintNum(null); setShiftReason(""); setShiftNote(""); }}>
              Cancel
            </Button>
            <span className="text-[10px] text-muted-foreground ml-auto">Reason logs to Decision Log automatically</span>
          </div>

          {/* Shift history for selected sprint */}
          {sprintShiftLog.has(selectedSprintNum!) && (
            <div className="border-t border-border pt-3 space-y-1.5">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Shift history — Sprint {selectedSprintNum}</span>
              {(sprintShiftLog.get(selectedSprintNum!) || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px]">
                  <span className="font-mono font-bold text-amber-500">{s.delta > 0 ? "+" : ""}{Math.abs(s.delta) % 7 === 0 ? `${s.delta / 7}w` : `${s.delta}d`}</span>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-foreground">{s.reason}</span>
                  <span className="text-muted-foreground/60 ml-auto">{s.appliedAt.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Global shift log — all sprints that have been shifted */}
      {sprintShiftLog.size > 0 && selectedSprintNum === null && (
        <div className="border border-border rounded-lg p-4 bg-card space-y-2">
          <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Sprint shift log</span>
          {Array.from(sprintShiftLog.entries()).map(([num, entries]) => (
            entries.map((s, i) => (
              <div key={`${num}-${i}`} className="flex items-center gap-2 text-[11px]">
                <span className="font-mono text-muted-foreground w-6">S{num}</span>
                <span className="font-mono font-bold text-amber-500">{s.delta > 0 ? "+" : ""}{Math.abs(s.delta) % 7 === 0 ? `${s.delta / 7}w` : `${s.delta}d`}</span>
                <span className="text-foreground flex-1">{s.reason}</span>
                <span className="text-muted-foreground/60">{s.appliedAt.slice(0, 10)}</span>
              </div>
            ))
          ))}
        </div>
      )}

      {stories.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Add user stories in the "User Stories" tab first, then come here to schedule them.
        </div>
      ) : (
        <>
          {/* Gantt Chart */}
          {scheduledStories.length > 0 && (
            <div className="border border-border rounded-md overflow-x-auto">
            <div className="min-w-[640px]">
              {/* Top band — sprints (day/week view) or month/quarter labels */}
              {(viewScale === "months" || viewScale === "quarters") ? (
                <div className="border-b border-border flex">
                  <div className="w-52 shrink-0 border-r border-border px-3 py-1 flex items-center">
                    <span className="text-[9px] font-mono text-muted-foreground uppercase">
                      {viewScale === "months" ? "months" : "quarters"}
                    </span>
                  </div>
                  <div className="flex-1 relative h-7">
                    {(() => {
                      const months = eachMonthOfInterval({ start: timelineStart, end: timelineEnd });
                      if (viewScale === "quarters") {
                        const quarters: { key: string; startPct: number; widthPct: number; label: string }[] = [];
                        const seen = new Set<string>();
                        months.forEach((m) => {
                          const q = getQuarter(m);
                          const y = getYear(m);
                          const qKey = `${y}-Q${q}`;
                          if (seen.has(qKey)) return;
                          seen.add(qKey);
                          const qStart = startOfMonth(new Date(y, (q - 1) * 3, 1));
                          const qEnd = startOfMonth(new Date(y, q * 3, 1));
                          const startOff = Math.max(differenceInDays(qStart, timelineStart), 0);
                          const endOff = Math.min(differenceInDays(qEnd, timelineStart), totalDays);
                          quarters.push({
                            key: qKey,
                            startPct: (startOff / totalDays) * 100,
                            widthPct: ((endOff - startOff) / totalDays) * 100,
                            label: `Q${q} ${y}`,
                          });
                        });
                        return quarters.map((q, i) => (
                          <div key={q.key} className={cn("absolute top-0 bottom-0 flex items-center justify-center text-[10px] font-mono font-bold border-r border-border/40", i % 2 === 0 ? "bg-accent/[0.06] text-accent" : "bg-primary/[0.04] text-primary")} style={{ left: `${q.startPct}%`, width: `${q.widthPct}%` }}>
                            {q.label}
                          </div>
                        ));
                      }
                      // months
                      return months.map((m, i) => {
                        const startOff = Math.max(differenceInDays(startOfMonth(m), timelineStart), 0);
                        const nextMonth = addMonths(m, 1);
                        const endOff = Math.min(differenceInDays(startOfMonth(nextMonth), timelineStart), totalDays);
                        return (
                          <div key={m.toISOString()} className={cn("absolute top-0 bottom-0 flex items-center justify-center text-[10px] font-mono font-bold border-r border-border/40", i % 2 === 0 ? "bg-accent/[0.06] text-accent" : "bg-primary/[0.04] text-primary")} style={{ left: `${(startOff / totalDays) * 100}%`, width: `${((endOff - startOff) / totalDays) * 100}%` }}>
                            {format(m, "MMM")}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              ) : sprints.length > 1 && (
                <div className="border-b border-border flex">
                  <div className="w-52 shrink-0 border-r border-border px-3 py-1 flex items-center">
                    <span className="text-[9px] font-mono text-muted-foreground uppercase">
                      {sprintLengthWeeks}w sprints
                    </span>
                  </div>
                  <div className="flex-1 relative h-7">
                    {sprints.map((sprint) => {
                      const hasShift = sprintShiftLog.has(sprint.number);
                      const shifts = sprintShiftLog.get(sprint.number) || [];
                      return (
                        <Tooltip key={sprint.number}>
                          <TooltipTrigger asChild>
                            <div
                              className={cn(
                                "absolute top-0 bottom-0 flex items-center justify-center text-[10px] font-mono font-bold border-r border-border/40 cursor-pointer hover:brightness-95 transition-all",
                                sprint.bgClass,
                                sprint.borderClass,
                                selectedSprintNum === sprint.number && "ring-2 ring-inset ring-accent/60"
                              )}
                              style={{ left: `${sprint.startPct}%`, width: `${sprint.widthPct}%` }}
                              onClick={() => setSelectedSprintNum(selectedSprintNum === sprint.number ? null : sprint.number)}
                            >
                              <span className="font-bold">{sprint.label}</span>
                              {hasShift && (
                                <span className="ml-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                              )}
                            </div>
                          </TooltipTrigger>
                          {hasShift && (
                            <TooltipContent className="max-w-xs text-xs space-y-1">
                              <p className="font-semibold">Shift history</p>
                              {shifts.map((s, i) => (
                                <p key={i} className="text-muted-foreground">
                                  {s.delta > 0 ? "+" : ""}{Math.abs(s.delta) % 7 === 0 ? `${s.delta / 7}w` : `${s.delta}d`} · {s.reason}
                                </p>
                              ))}
                              <p className="text-[10px] text-muted-foreground/60">Click to shift again</p>
                            </TooltipContent>
                          )}
                          {!hasShift && (
                            <TooltipContent className="text-xs">Click to shift {sprint.label} + cascades</TooltipContent>
                          )}
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Header - date markers */}
              <div className="bg-secondary/50 border-b border-border flex">
                <div className="w-52 shrink-0 px-3 py-2 text-[10px] font-mono text-muted-foreground uppercase border-r border-border">
                  Story
                </div>
                <div className="flex-1 relative h-8">
                  {/* Sprint boundary lines in date header */}
                  {sprints.map((sprint) => (
                    <div
                      key={`hdr-${sprint.number}`}
                      className="absolute top-0 bottom-0 border-r border-accent/20"
                      style={{ left: `${sprint.startPct + sprint.widthPct}%` }}
                    />
                  ))}
                  {/* Milestone position ticks in date header */}
                  {milestones.map(m => {
                    const pct = (differenceInDays(parseISO(m.date), timelineStart) / totalDays) * 100;
                    if (pct < 0 || pct > 100) return null;
                    return (
                      <div key={m.id} className="absolute top-0 bottom-0 w-px"
                        style={{ left: `${pct}%`, backgroundColor: "#314E6E", opacity: 0.3 }} />
                    );
                  })}
                  {(() => {
                    if (viewScale === "months" || viewScale === "quarters") {
                      const months = eachMonthOfInterval({ start: timelineStart, end: timelineEnd });
                      const ticks: { key: string; left: number; label: string }[] = [];
                      if (viewScale === "months") {
                        months.forEach((m) => {
                          const dayOffset = differenceInDays(startOfMonth(m), timelineStart);
                          if (dayOffset < 0 || dayOffset > totalDays) return;
                          ticks.push({
                            key: m.toISOString(),
                            left: (dayOffset / totalDays) * 100,
                            label: format(m, "MMM yyyy"),
                          });
                        });
                      } else {
                        // quarters — emit a tick at start of each quarter
                        const seen = new Set<string>();
                        months.forEach((m) => {
                          const q = getQuarter(m);
                          const y = getYear(m);
                          const qKey = `${y}-Q${q}`;
                          if (seen.has(qKey)) return;
                          seen.add(qKey);
                          const qStart = startOfMonth(new Date(y, (q - 1) * 3, 1));
                          const dayOffset = differenceInDays(qStart, timelineStart);
                          if (dayOffset < 0 || dayOffset > totalDays) return;
                          ticks.push({
                            key: qKey,
                            left: (dayOffset / totalDays) * 100,
                            label: `Q${q} ${y}`,
                          });
                        });
                      }
                      return ticks.map((t) => (
                        <span
                          key={t.key}
                          className="absolute text-[9px] font-mono text-muted-foreground top-2 whitespace-nowrap"
                          style={{ left: `${t.left}%`, transform: "translateX(-50%)" }}
                        >
                          {t.label}
                        </span>
                      ));
                    }
                    if (viewScale === "weeks") {
                      const ticks: { key: string; left: number; label: string }[] = [];
                      let cursor = startOfWeek(timelineStart, { weekStartsOn: 1 });
                      // thin out ticks for long timelines: every 2w if >90d, every 4w if >180d
                      const stepWeeks = totalDays > 180 ? 4 : totalDays > 90 ? 2 : 1;
                      let weekIndex = 0;
                      while (!isAfter(cursor, timelineEnd)) {
                        const dayOffset = differenceInDays(cursor, timelineStart);
                        if (dayOffset >= 0 && dayOffset <= totalDays && weekIndex % stepWeeks === 0) {
                          ticks.push({
                            key: cursor.toISOString(),
                            left: (dayOffset / totalDays) * 100,
                            label: format(cursor, "MMM d"),
                          });
                        }
                        cursor = addWeeks(cursor, 1);
                        weekIndex++;
                      }
                      return ticks.map((t) => (
                        <span key={t.key} className="absolute text-[9px] font-mono text-muted-foreground top-2 whitespace-nowrap" style={{ left: `${t.left}%`, transform: "translateX(-50%)" }}>
                          {t.label}
                        </span>
                      ));
                    }
                    // Default: days — step size grows with timeline length to avoid crowding
                    const dayStep = totalDays > 365 ? 28 : totalDays > 180 ? 21 : totalDays > 90 ? 14 : totalDays > 30 ? 7 : totalDays > 14 ? 2 : 1;
                    return Array.from({ length: totalDays + 1 }).map((_, i) => {
                      if (i % dayStep !== 0) return null;
                      const d = addDays(timelineStart, i);
                      const left = (i / totalDays) * 100;
                      return (
                        <span key={i} className="absolute text-[9px] font-mono text-muted-foreground top-2 whitespace-nowrap" style={{ left: `${left}%`, transform: "translateX(-50%)" }}>
                          {totalDays > 180 ? format(d, "MMM ''yy") : format(d, "MMM d")}
                        </span>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Milestone row */}
              {milestones.length > 0 && (
                <div className="flex border-b border-border" style={{ backgroundColor: "#F7F4EE" }}>
                  <div className="w-52 shrink-0 px-3 py-1 border-r border-border flex items-center">
                    <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: "#9B978F" }}>Milestones</span>
                  </div>
                  <div className="flex-1 relative" style={{ height: 28 }}>
                    {sprints.map((sprint) => (
                      <div key={`ms-bg-${sprint.number}`}
                        className={cn("absolute top-0 bottom-0 border-r border-border/30", sprint.bgClass)}
                        style={{ left: `${sprint.startPct}%`, width: `${sprint.widthPct}%` }}
                      />
                    ))}
                    {milestones.map(m => {
                      const pct = (differenceInDays(parseISO(m.date), timelineStart) / totalDays) * 100;
                      if (pct < 0 || pct > 100) return null;
                      return (
                        <Tooltip key={m.id}>
                          <TooltipTrigger asChild>
                            <div className="absolute flex items-center gap-1 cursor-default z-10"
                              style={{ left: `${pct}%`, top: "50%", transform: "translateY(-50%)" }}>
                              <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "#314E6E" }} />
                              <span className="text-[10px] font-mono font-semibold whitespace-nowrap"
                                style={{ color: "#314E6E" }}>
                                {m.name.length > 22 ? m.name.slice(0, 20) + "…" : m.name}
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs font-semibold">{m.name}</p>
                            <p className="text-[10px] text-muted-foreground">{format(parseISO(m.date), "MMM d, yyyy")}</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Rows */}
              {scheduledStories
                .sort((a, b) => parseISO(a.startDate!).getTime() - parseISO(b.startDate!).getTime())
                .map((story) => {
                  const start = parseISO(story.startDate!);
                  const end = parseISO(story.dueDate!);
                  const leftPct = (differenceInDays(start, timelineStart) / totalDays) * 100;
                  const widthPct = Math.max((differenceInDays(end, start) / totalDays) * 100, 2);
                  const isSelected = selectedStoryId === story.id;
                  const hasShifted = story.originalDueDate && story.dueDate !== story.originalDueDate;
                  const daysDiff = story.originalDueDate
                    ? differenceInDays(parseISO(story.dueDate!), parseISO(story.originalDueDate))
                    : 0;

                  return (
                    <div
                      key={story.id}
                      className={cn(
                        "flex border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors cursor-pointer",
                        isSelected && "bg-accent/5",
                        linkingFrom && linkingFrom !== story.id && "hover:bg-accent/10"
                      )}
                      onClick={() => {
                        if (linkingFrom && linkingFrom !== story.id) {
                          handleAddDependency(linkingFrom, story.id);
                        } else {
                          setSelectedStoryId(isSelected ? null : story.id);
                        }
                      }}
                    >
                      {/* Label */}
                      <div className="w-52 shrink-0 px-3 py-2 border-r border-border">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-card-foreground truncate font-body">
                            {story.title}
                          </span>
                          {hasShifted && (
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className={cn("h-3 w-3 shrink-0", daysDiff > 0 ? "text-destructive" : "text-accent")} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">
                                  {daysDiff > 0 ? `Pushed ${daysDiff}d from original` : `Pulled ${Math.abs(daysDiff)}d earlier`}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {story.storyPoints && (
                            <span className="text-[10px] font-mono text-muted-foreground">{story.storyPoints}pts</span>
                          )}
                          {(() => {
                            const d = story.estimatedDays || estimateDays(story.storyPoints);
                            const label = d >= 7 ? `${Math.round(d / 7)}w` : `${d}d`;
                            return <span className="text-[10px] text-muted-foreground">{label}</span>;
                          })()}
                          {(story.dependencies?.length || 0) > 0 && (
                            <span className="text-[10px] text-accent flex items-center gap-0.5">
                              <Link2 className="h-2.5 w-2.5" />
                              {story.dependencies!.length}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bar */}
                      <div className="flex-1 relative py-2 px-1">
                        {/* Sprint background bands */}
                        {sprints.map((sprint) => (
                          <div
                            key={`bg-${sprint.number}`}
                            className={cn(
                              "absolute top-0 bottom-0 border-r border-border/30",
                              sprint.bgClass
                            )}
                            style={{ left: `${sprint.startPct}%`, width: `${sprint.widthPct}%` }}
                          />
                        ))}
                        {/* Today marker */}
                        {differenceInDays(today, timelineStart) >= 0 && differenceInDays(today, timelineStart) <= totalDays && (
                          <div
                            className="absolute top-0 bottom-0 w-px bg-destructive/30 z-10"
                            style={{ left: `${(differenceInDays(today, timelineStart) / totalDays) * 100}%` }}
                          />
                        )}
                        {/* Milestone vertical lines */}
                        {milestones.map(m => {
                          const pct = (differenceInDays(parseISO(m.date), timelineStart) / totalDays) * 100;
                          if (pct < 0 || pct > 100) return null;
                          return (
                            <div
                              key={`ml-${m.id}`}
                              className="absolute top-0 bottom-0 w-px z-10"
                              style={{ left: `${pct}%`, backgroundColor: "#314E6E", opacity: 0.35 }}
                            />
                          );
                        })}

                        {/* Original position ghost bar */}
                        {hasShifted && story.originalDueDate && (
                          <div
                            className="absolute h-5 top-1/2 -translate-y-1/2 rounded-sm bg-muted/40 border border-dashed border-muted-foreground/20"
                            style={{
                              left: `${leftPct}%`,
                              width: `${Math.max((differenceInDays(parseISO(story.originalDueDate), start) / totalDays) * 100, 1)}%`,
                            }}
                          />
                        )}

                        {/* Actual bar */}
                        <div
                          className={cn(
                            "absolute h-5 top-1/2 -translate-y-1/2 rounded-sm text-[9px] font-mono flex items-center px-1.5 overflow-hidden transition-all",
                            story.priority === "must"
                              ? "bg-accent text-accent-foreground"
                              : story.priority === "should"
                              ? "bg-primary/80 text-primary-foreground"
                              : "bg-secondary text-secondary-foreground",
                            hasShifted && daysDiff > 0 && "ring-1 ring-destructive/50"
                          )}
                          style={{
                            left: `${Math.max(leftPct, 0)}%`,
                            width: `${Math.min(widthPct, 100 - leftPct)}%`,
                          }}
                        >
                          {widthPct >= 8 && (
                            <span className="truncate">
                              {format(start, "MMM d")} – {format(end, "MMM d")}
                            </span>
                          )}
                        </div>

                        {/* Dependency arrows */}
                        {story.dependencies?.map((depId) => {
                          const dep = storyById.get(depId);
                          if (!dep?.dueDate) return null;
                          const depEndPct = (differenceInDays(parseISO(dep.dueDate), timelineStart) / totalDays) * 100;
                          return (
                            <div
                              key={depId}
                              className="absolute top-1/2 h-px bg-accent/40"
                              style={{
                                left: `${Math.min(depEndPct, leftPct)}%`,
                                width: `${Math.abs(leftPct - depEndPct)}%`,
                              }}
                            >
                              <ArrowRight className="h-2.5 w-2.5 text-accent/60 absolute right-0 -top-[4px]" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
            </div>
          )}

          {/* Detail panel for selected story */}
          {selectedStoryId && (() => {
            const story = storyById.get(selectedStoryId);
            if (!story) return null;
            const hasShifted = story.originalDueDate && story.dueDate !== story.originalDueDate;
            const daysDiff = story.originalDueDate && story.dueDate
              ? differenceInDays(parseISO(story.dueDate), parseISO(story.originalDueDate))
              : 0;

            return (
              <div className="border border-border rounded-md p-4 bg-card space-y-3 animate-fade-in">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-sm">{story.title}</h3>
                  <div className="flex gap-1.5 items-center">
                    {linkingFrom === selectedStoryId && (
                      <span className="text-[10px] text-accent font-medium animate-pulse">
                        ← now click another task to link
                      </span>
                    )}
                    <Button
                      variant={linkingFrom === selectedStoryId ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-[10px] gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLinkingFrom(linkingFrom === selectedStoryId ? null : selectedStoryId);
                      }}
                    >
                      <Link2 className="h-3 w-3" /> {linkingFrom === selectedStoryId ? "Cancel Link" : "Add Dependency"}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <DatePickerField
                    label="Start Date"
                    value={story.startDate ? parseISO(story.startDate) : undefined}
                    onChange={(d) => d && handleDateChange(selectedStoryId, "startDate", d)}
                  />
                  <DatePickerField
                    label="Due Date"
                    value={story.dueDate ? parseISO(story.dueDate) : undefined}
                    onChange={(d) => d && handleDateChange(selectedStoryId, "dueDate", d)}
                  />
                  <div>
                    <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
                      Est. Days
                    </label>
                    <span className="text-sm font-semibold font-body">
                      {story.estimatedDays || estimateDays(story.storyPoints)}d
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-1">
                      ({story.storyPoints || 0} pts)
                    </span>
                  </div>
                  <div>
                    <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
                      Shift
                    </label>
                    {hasShifted ? (
                      <span className={cn("text-sm font-semibold font-body", daysDiff > 0 ? "text-destructive" : "text-accent")}>
                        {daysDiff > 0 ? `+${daysDiff}` : daysDiff} days
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">On track</span>
                    )}
                  </div>
                </div>

                {/* Dependencies */}
                {(story.dependencies?.length || 0) > 0 && (
                  <div>
                    <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
                      Depends On
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {story.dependencies!.map((depId) => {
                        const dep = storyById.get(depId);
                        return (
                          <Badge
                            key={depId}
                            variant="outline"
                            className="text-[10px] gap-1 pr-1"
                          >
                            {dep?.title || depId}
                            <button
                              onClick={() => removeDependency(story.id, depId)}
                              className="hover:text-destructive ml-0.5"
                            >
                              ✕
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Unscheduled stories */}
          {unscheduledStories.length > 0 && (
            <div>
              <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Unscheduled ({unscheduledStories.length})
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {unscheduledStories.map((story) => (
                  <div
                    key={story.id}
                    className="border border-dashed border-border rounded-md p-3 bg-card/50 text-xs"
                  >
                    <span className="font-semibold font-body">{story.title}</span>
                    <span className="text-muted-foreground ml-2">
                      {story.storyPoints || 0}pts → ~{estimateDays(story.storyPoints)}d suggested
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Click "Auto-Schedule" to assign dates to all unscheduled stories.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Date Picker Field ─── */
function DatePickerField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: Date;
  onChange: (d: Date | undefined) => void;
}) {
  return (
    <div>
      <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
        {label}
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full h-8 justify-start text-left text-xs font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-3 w-3 mr-1.5" />
            {value ? format(value, "MMM d, yyyy") : "Pick date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
